import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TrackingEventAction, TrackingEventCategory } from '@izimi/modules';
import { Tracker } from '@izimi/modules';

import { useTrackingStore } from './trackingStore';

interface TrackEvent {
  action: TrackingEventAction;
  category: TrackingEventCategory;
  name?: string;
  value?: number;
}

const useTracking = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const { matomoTracker } = useTrackingStore();

  const customDimensions = useMemo(
    () => [
      {
        id: Tracker.WebPageViewCustomDimensionId.Language,
        value: language,
      },
    ],
    [language],
  );

  const trackPageView = useCallback(
    (url: string) => {
      matomoTracker?.trackPageView({ href: url, customDimensions });
    },
    [matomoTracker, customDimensions],
  );

  const trackEvent = useCallback(
    ({ category, action, name = '', value = undefined }: TrackEvent) => {
      matomoTracker?.trackEvent({
        category: category as unknown as string,
        action: action as unknown as string,
        name,
        value,
      });
    },
    [matomoTracker],
  );

  return {
    trackPageView,
    trackEvent,
  };
};

export default useTracking;
